import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Mail, Plus, Send, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATE_TABS = [
  ["welcome", "Welcome Email"],
  ["blast", "Drop-Day Blast"],
  ["nudge", "Nudge Email"],
  ["receipt", "Order Receipt"],
  ["alert", "Admin Alerts"],
  ["portal", "Manage Link"],
  ["shipped", "Shipping Email"],
  ["referral_invite", "Referral Code"],
  ["referral_reward", "Referral Reward"],
  ["review", "Review Request"],
  ["survey", "Post-Purchase Survey"],
  ["abandoned", "Abandoned Checkout"],
  ["popup_nudge", "Popup Nudge"],
];

const TEMPLATE_NOTES = {
  welcome: "Sends the founders welcome with the intro-offer perks.",
  blast: "The drop-day announcement, personalised to each recipient.",
  nudge: "The founders reminder — same email the Nudge campaign sends.",
  receipt: "Uses their latest paid order; falls back to a sample receipt if they have none.",
  alert: "Admin alert layout with sample content — mostly useful for previewing.",
  portal: "Includes a REAL 24-hour subscription manage link for that email.",
  shipped: "Uses their latest posted order's tracking; sample tracking if none.",
  referral_invite: "Uses their active referral code if they have one; sample code otherwise.",
  referral_reward: "Uses their active reward code if they have one; sample code otherwise.",
  review: "Creates a REAL one-click review link — skipped for people with no order.",
  survey: "The post-purchase survey with their email pre-filled.",
  abandoned: "Links back to their open checkout if one exists; otherwise the buy page.",
  popup_nudge: "Fills in their real 10% popup code if they're a captured lead.",
};

const inputCls =
  "w-full h-12 bg-transparent border border-white/20 focus:border-volt px-4 font-plex text-sm text-white outline-none transition-colors duration-300 placeholder:text-white/30";
const textareaCls =
  "w-full bg-transparent border border-white/20 focus:border-volt px-4 py-3 font-plex text-sm text-white outline-none transition-colors duration-300 placeholder:text-white/30";
const labelCls = "block font-plex text-[10px] uppercase tracking-[0.25em] text-white/40 mb-2";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const RecipientPicker = ({ testid, recipients, selected, onChange }) => {
  const [custom, setCustom] = useState("");
  const groups = [
    ["customers", "Customers"],
    ["leads", "Email captures"],
    ["founders", "Founders"],
  ];
  const toggle = (email) => {
    const next = new Set(selected);
    if (next.has(email)) next.delete(email);
    else next.add(email);
    onChange(next);
  };
  const setGroup = (key, on) => {
    const next = new Set(selected);
    (recipients?.[key] || []).forEach((r) => (on ? next.add(r.email) : next.delete(r.email)));
    onChange(next);
  };
  const addCustom = () => {
    const e = custom.trim().toLowerCase();
    if (!EMAIL_RE.test(e)) {
      toast.error("Enter a valid email address.");
      return;
    }
    if (!selected.has(e)) {
      const next = new Set(selected);
      next.add(e);
      onChange(next);
    }
    setCustom("");
  };
  const customSelected = [...selected].filter(
    (e) => !groups.some(([key]) => (recipients?.[key] || []).some((r) => r.email === e))
  );

  return (
    <div data-testid={testid}>
      <div className="flex flex-col md:flex-row gap-3">
        <input
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addCustom()}
          placeholder="TYPE ANY EMAIL ADDRESS"
          data-testid={`${testid}-custom-input`}
          className={`${inputCls} md:max-w-sm`}
        />
        <button
          onClick={addCustom}
          data-testid={`${testid}-custom-add`}
          className="h-12 px-6 border border-white/25 text-white font-anton uppercase tracking-wider text-sm transition-[background-color,color,transform] duration-200 hover:bg-white hover:text-black active:scale-95 flex items-center gap-2"
        >
          <Plus size={14} /> Add
        </button>
      </div>

      {customSelected.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {customSelected.map((e) => (
            <span
              key={e}
              data-testid={`${testid}-chip-${e}`}
              className="flex items-center gap-2 border border-volt/60 text-volt font-plex text-xs px-3 py-1.5"
            >
              {e}
              <button onClick={() => toggle(e)} aria-label={`Remove ${e}`} className="hover:text-white">
                <X size={12} />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-4">
        {groups.map(([key, label]) => {
          const rows = recipients?.[key] || [];
          const allOn = rows.length > 0 && rows.every((r) => selected.has(r.email));
          return (
            <div key={key} className="border border-white/10" data-testid={`${testid}-group-${key}`}>
              <button
                onClick={() => setGroup(key, !allOn)}
                data-testid={`${testid}-group-${key}-all`}
                className="w-full flex items-center justify-between px-4 h-11 font-plex text-[10px] uppercase tracking-[0.25em] text-white/50 hover:text-volt transition-colors"
              >
                <span>
                  {label} ({rows.length})
                </span>
                <span className={allOn ? "text-volt" : ""}>{allOn ? "Clear all" : "Select all"}</span>
              </button>
              <div className="max-h-56 overflow-y-auto border-t border-white/10">
                {rows.length === 0 && (
                  <p className="px-4 py-3 text-white/30 font-light text-sm">Nobody here yet.</p>
                )}
                {rows.map((r, i) => {
                  const on = selected.has(r.email);
                  return (
                    <button
                      key={r.email}
                      onClick={() => toggle(r.email)}
                      data-testid={`${testid}-${key}-${i}`}
                      className={`w-full text-left px-4 py-2.5 border-b border-white/5 transition-colors duration-150 ${
                        on ? "bg-volt/10" : "hover:bg-white/5"
                      }`}
                    >
                      <span className={`font-plex text-xs ${on ? "text-volt" : "text-white"}`}>{r.email}</span>
                      <span className="block text-white/30 font-light text-[11px]">{r.detail}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const EmailSuite = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [auth, setAuth] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(!location.state?.user);

  const [templates, setTemplates] = useState(null);
  const [editTab, setEditTab] = useState("welcome");
  const [savingTemplate, setSavingTemplate] = useState(false);

  const [recipients, setRecipients] = useState(null);
  const [sendTab, setSendTab] = useState("welcome");
  const [sendSel, setSendSel] = useState(new Set());
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [sendResults, setSendResults] = useState(null);

  const [custom, setCustom] = useState({
    subject: "",
    overline: "",
    headline: "",
    message: "",
    cta_label: "",
    cta_url: "",
  });
  const [customSel, setCustomSel] = useState(new Set());
  const [sendingCustom, setSendingCustom] = useState(false);

  const [armed, setArmed] = useState(null);

  useEffect(() => {
    if (location.state?.user) return;
    const checkAuth = async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setAuth(data);
      } catch {
        setAuth(false);
      } finally {
        setChecking(false);
      }
    };
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (auth === false) navigate("/admin", { state: { authError: "Sign in to open the Email Suite." } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  useEffect(() => {
    if (!auth) return;
    const load = async () => {
      try {
        const [{ data: t }, { data: r }] = await Promise.all([
          axios.get(`${API}/email/templates`, { withCredentials: true }),
          axios.get(`${API}/email/recipients`, { withCredentials: true }),
        ]);
        setTemplates(t);
        setRecipients(r);
      } catch {
        toast.error("Could not load the Email Suite.");
      }
    };
    load();
  }, [auth]);

  const arm = (key) => {
    setArmed(key);
    setTimeout(() => setArmed(null), 4000);
  };

  const updateField = (tab, field, value) =>
    setTemplates((t) => ({ ...t, [tab]: { ...t[tab], [field]: value } }));

  const onSaveTemplate = async () => {
    setSavingTemplate(true);
    try {
      const { data } = await axios.put(`${API}/email/templates`, templates, { withCredentials: true });
      setTemplates(data);
      toast.success("Template saved.");
    } catch {
      toast.error("Save failed.");
    } finally {
      setSavingTemplate(false);
    }
  };

  const onSendTemplate = async () => {
    if (sendSel.size === 0) {
      toast.error("Pick at least one recipient.");
      return;
    }
    if (armed !== "template") return arm("template");
    setArmed(null);
    setSendingTemplate(true);
    setSendResults(null);
    try {
      await axios.put(`${API}/email/templates`, templates, { withCredentials: true });
      const { data } = await axios.post(
        `${API}/email/send`,
        { template: sendTab, recipients: [...sendSel] },
        { withCredentials: true }
      );
      setSendResults(data.results);
      toast.success(`Sent to ${data.sent}/${data.total}${data.failed ? ` — ${data.failed} failed` : ""}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Send failed.");
    } finally {
      setSendingTemplate(false);
    }
  };

  const onSendCustom = async () => {
    if (!custom.subject.trim() || !custom.message.trim()) {
      toast.error("Custom email needs a subject and a message.");
      return;
    }
    if (customSel.size === 0) {
      toast.error("Pick at least one recipient.");
      return;
    }
    if (armed !== "custom") return arm("custom");
    setArmed(null);
    setSendingCustom(true);
    try {
      const { data } = await axios.post(
        `${API}/email/custom`,
        { ...custom, recipients: [...customSel] },
        { withCredentials: true }
      );
      toast.success(`Custom email sent to ${data.sent}/${data.total}${data.failed ? ` — ${data.failed} failed` : ""}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Send failed.");
    } finally {
      setSendingCustom(false);
    }
  };

  if (checking || !auth) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white font-manrope flex items-center justify-center">
        <p className="font-plex text-xs uppercase tracking-[0.25em] text-white/40">Loading…</p>
      </div>
    );
  }

  const tabBtn = (key, label, active, setActive, prefix) => (
    <button
      key={key}
      data-testid={`${prefix}-${key}`}
      onClick={() => setActive(key)}
      className={`px-4 h-10 font-anton uppercase tracking-wider text-xs transition-[background-color,color,transform,border-color] duration-200 active:scale-95 ${
        active === key
          ? "bg-volt text-black"
          : "border border-white/20 text-white/60 hover:text-white hover:border-white/50"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div data-testid="email-suite" className="min-h-screen bg-[#0A0A0A] text-white font-manrope">
      <header className="border-b border-white/10 px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <img src="/breethe-logo.webp" alt="Breethe" className="h-6 w-auto" />
          <span className="font-plex text-[10px] uppercase tracking-[0.3em] text-volt">Email Suite</span>
        </div>
        <button
          onClick={() => navigate("/admin")}
          data-testid="email-suite-back"
          className="h-10 px-4 border border-white/15 flex items-center gap-2 font-plex text-xs uppercase tracking-[0.2em] text-white/60 hover:text-volt hover:border-volt transition-colors active:scale-95"
        >
          <ArrowLeft size={14} /> Back to HQ
        </button>
      </header>

      <main className="px-6 md:px-10 py-10 md:py-14 max-w-[1600px] mx-auto">
        <p className="font-plex text-xs uppercase tracking-[0.25em] text-white/40">Founders HQ</p>
        <h1 className="mt-2 font-anton uppercase text-4xl md:text-6xl leading-[0.9]">
          Email suite<span className="text-volt">.</span>
        </h1>
        <p className="mt-4 text-white/60 font-light text-sm md:text-base max-w-2xl">
          Edit every email the site sends, fire any template at individual customers or email
          captures, or write a one-off in the same Breethe design.
        </p>

        {/* ── Template editor ─────────────────────────────────────────── */}
        <div data-testid="suite-editor" className="mt-12 border border-white/10 bg-[#121212] p-6 md:p-8">
          <p className="font-anton uppercase text-xl md:text-2xl flex items-center gap-3">
            <Mail size={22} className="text-volt" /> Edit templates
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {TEMPLATE_TABS.map(([key, label]) => tabBtn(key, label, editTab, setEditTab, "suite-template-tab"))}
          </div>
          {templates && (
            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label className={labelCls}>Subject</label>
                <input
                  value={templates[editTab].subject}
                  onChange={(e) => updateField(editTab, "subject", e.target.value)}
                  data-testid="suite-template-subject-input"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Overline (small red label)</label>
                <input
                  value={templates[editTab].overline}
                  onChange={(e) => updateField(editTab, "overline", e.target.value)}
                  data-testid="suite-template-overline-input"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Button label — leave empty for no button</label>
                <input
                  value={templates[editTab].cta_label}
                  onChange={(e) => updateField(editTab, "cta_label", e.target.value)}
                  data-testid="suite-template-cta-label-input"
                  className={inputCls}
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Headline — new line = line break</label>
                <textarea
                  rows={2}
                  value={templates[editTab].headline}
                  onChange={(e) => updateField(editTab, "headline", e.target.value)}
                  data-testid="suite-template-headline-input"
                  className={textareaCls}
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Message — blank line = new paragraph</label>
                <textarea
                  rows={4}
                  value={templates[editTab].message}
                  onChange={(e) => updateField(editTab, "message", e.target.value)}
                  data-testid="suite-template-message-input"
                  className={textareaCls}
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Button link (e.g. /buy or https://…)</label>
                <input
                  value={templates[editTab].cta_url}
                  onChange={(e) => updateField(editTab, "cta_url", e.target.value)}
                  data-testid="suite-template-cta-url-input"
                  className={inputCls}
                />
              </div>
            </div>
          )}
          <button
            onClick={onSaveTemplate}
            disabled={savingTemplate || !templates}
            data-testid="suite-template-save-button"
            className="mt-6 h-12 px-8 bg-volt text-black font-anton uppercase tracking-wider text-sm transition-[background-color,transform] duration-200 hover:bg-volt-dark active:scale-95 disabled:opacity-60"
          >
            {savingTemplate ? "Saving…" : "Save Template"}
          </button>
        </div>

        {/* ── Manual template send ────────────────────────────────────── */}
        <div data-testid="suite-manual-send" className="mt-10 border border-white/10 bg-[#121212] p-6 md:p-8">
          <p className="font-anton uppercase text-xl md:text-2xl flex items-center gap-3">
            <Send size={22} className="text-volt" /> Send a template
          </p>
          <p className="mt-2 text-white/60 font-light text-sm md:text-base max-w-xl">
            Pick a template, tick the people who should get it, send. Real data is used wherever
            possible — their order, their tracking, their referral code.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {TEMPLATE_TABS.map(([key, label]) => tabBtn(key, label, sendTab, setSendTab, "suite-send-tab"))}
          </div>
          <p data-testid="suite-send-note" className="mt-4 font-plex text-xs text-volt/80 tracking-wide">
            {TEMPLATE_NOTES[sendTab]}
          </p>
          <div className="mt-4">
            <RecipientPicker testid="suite-send-recipients" recipients={recipients} selected={sendSel} onChange={setSendSel} />
          </div>
          <button
            onClick={onSendTemplate}
            disabled={sendingTemplate}
            data-testid="suite-send-button"
            className={`mt-6 h-12 px-8 font-anton uppercase tracking-wider text-sm transition-[background-color,color,transform] duration-200 active:scale-95 disabled:opacity-60 ${
              armed === "template" ? "bg-white text-black" : "bg-volt text-black hover:bg-volt-dark"
            }`}
          >
            {sendingTemplate
              ? "Sending…"
              : armed === "template"
                ? `Really send to ${sendSel.size}? Click again`
                : `Send to ${sendSel.size} selected`}
          </button>
          {sendResults && (
            <div data-testid="suite-send-results" className="mt-5 border border-white/10">
              {sendResults.map((r, i) => (
                <div
                  key={r.email}
                  data-testid={`suite-send-result-${i}`}
                  className="flex flex-wrap items-baseline gap-x-3 px-4 py-2.5 border-b border-white/5"
                >
                  <span className="font-plex text-xs text-white">{r.email}</span>
                  <span
                    className={`font-plex text-[10px] uppercase tracking-[0.2em] ${
                      r.status === "sent" ? "text-volt" : r.status === "skipped" ? "text-yellow-500" : "text-red-500"
                    }`}
                  >
                    {r.status}
                  </span>
                  {r.note && <span className="text-white/30 font-light text-[11px]">{r.note}</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Custom one-off email ────────────────────────────────────── */}
        <div data-testid="suite-custom" className="mt-10 border border-white/10 bg-[#121212] p-6 md:p-8">
          <p className="font-anton uppercase text-xl md:text-2xl flex items-center gap-3">
            <Plus size={22} className="text-volt" /> Custom email
          </p>
          <p className="mt-2 text-white/60 font-light text-sm md:text-base max-w-xl">
            Write a one-off and it goes out in the same dark Breethe design as every other email.
          </p>
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 content-start">
              <div className="md:col-span-2">
                <label className={labelCls}>Subject</label>
                <input
                  value={custom.subject}
                  onChange={(e) => setCustom((c) => ({ ...c, subject: e.target.value }))}
                  data-testid="custom-subject-input"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Overline (small red label)</label>
                <input
                  value={custom.overline}
                  onChange={(e) => setCustom((c) => ({ ...c, overline: e.target.value }))}
                  data-testid="custom-overline-input"
                  className={inputCls}
                />
              </div>
              <div>
                <label className={labelCls}>Button label — optional</label>
                <input
                  value={custom.cta_label}
                  onChange={(e) => setCustom((c) => ({ ...c, cta_label: e.target.value }))}
                  data-testid="custom-cta-label-input"
                  className={inputCls}
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Headline — new line = line break</label>
                <textarea
                  rows={2}
                  value={custom.headline}
                  onChange={(e) => setCustom((c) => ({ ...c, headline: e.target.value }))}
                  data-testid="custom-headline-input"
                  className={textareaCls}
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Message — blank line = new paragraph</label>
                <textarea
                  rows={5}
                  value={custom.message}
                  onChange={(e) => setCustom((c) => ({ ...c, message: e.target.value }))}
                  data-testid="custom-message-input"
                  className={textareaCls}
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelCls}>Button link — optional (e.g. /buy or https://…)</label>
                <input
                  value={custom.cta_url}
                  onChange={(e) => setCustom((c) => ({ ...c, cta_url: e.target.value }))}
                  data-testid="custom-cta-url-input"
                  className={inputCls}
                />
              </div>
            </div>

            <div>
              <label className={labelCls}>Preview</label>
              <div data-testid="custom-preview" className="border border-white/20 bg-[#0A0A0A] p-6">
                <div className="border border-[#2A2A2A] bg-[#121212]">
                  <div className="px-6 py-4 border-b border-[#2A2A2A]">
                    <span className="font-anton text-lg tracking-[4px] text-white">
                      BREETHE<span className="text-volt">®</span>
                    </span>
                  </div>
                  <div className="p-6">
                    <p className="font-plex text-[10px] tracking-[3px] text-volt uppercase">
                      {custom.overline || "Your overline"}
                    </p>
                    <h2 className="mt-3 font-anton uppercase text-2xl leading-[1.05] text-white">
                      {(custom.headline || "Your headline").split("\n").map((line, i) => (
                        <span key={i} className="block">{line}</span>
                      ))}
                    </h2>
                    <div className="mt-4 space-y-3">
                      {(custom.message || "Your message appears here.").split("\n").filter((l) => l.trim()).map((line, i) => (
                        <p key={i} className="text-[#A1A1AA] text-sm font-light leading-relaxed">{line}</p>
                      ))}
                    </div>
                    {custom.cta_label.trim() && (
                      <span className="mt-5 inline-block bg-volt text-black font-anton uppercase tracking-wider text-xs px-6 py-3">
                        {custom.cta_label}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6">
            <label className={labelCls}>Recipients</label>
            <RecipientPicker testid="custom-recipients" recipients={recipients} selected={customSel} onChange={setCustomSel} />
          </div>
          <button
            onClick={onSendCustom}
            disabled={sendingCustom}
            data-testid="custom-send-button"
            className={`mt-6 h-12 px-8 font-anton uppercase tracking-wider text-sm transition-[background-color,color,transform] duration-200 active:scale-95 disabled:opacity-60 ${
              armed === "custom" ? "bg-white text-black" : "bg-volt text-black hover:bg-volt-dark"
            }`}
          >
            {sendingCustom
              ? "Sending…"
              : armed === "custom"
                ? `Really send to ${customSel.size}? Click again`
                : `Send custom email to ${customSel.size} selected`}
          </button>
        </div>
      </main>
    </div>
  );
};

export default EmailSuite;
